/**
 * Issue #197 — 员工管理工作区的纯逻辑 helper（便于单测）。
 *
 * 关注点：列隐藏只能落在后端列白名单内（且不可隐藏列恒可见）、分页页大小限定
 * 20/50/100、筛选状态到后端查询参数的映射。组件只负责状态与渲染，决策逻辑全部在
 * 这里且可测。员工列隐藏是**纯展示层**关注点：列 key 是逻辑列（如 employee 聚合
 * 头像+姓名+工号），后端 items 始终返回完整视图，故 columns 不进入查询参数。
 */
import type {
  AdminEmployeesListQuery,
  EmployeeColumnMeta,
  EmployeeStatus,
  Role,
} from './types';

export const EMPLOYEE_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export const EMPLOYEE_DEFAULT_PAGE_SIZE = 20;

/**
 * 计算可见列：优先使用持久化集，否则用后端 defaultVisible 集；两者都**只在后端列
 * 白名单内取交集**，并**强制包含不可隐藏列**（employee / actions）。返回按白名单顺序
 * 稳定排序，杜绝引入非白名单列或隐藏主体列。columns 未加载（空）时返回 []。
 */
export function resolveVisibleColumns(
  preferred: string[] | null | undefined,
  columns: EmployeeColumnMeta[],
): string[] {
  if (columns.length === 0) return [];
  const order = columns.map((c) => c.key);
  const whitelist = new Set(order);
  const nonHideable = new Set(columns.filter((c) => !c.hideable).map((c) => c.key));
  const base =
    preferred && preferred.length > 0
      ? preferred
      : columns.filter((c) => c.defaultVisible).map((c) => c.key);
  const baseSet = new Set(base.filter((k) => whitelist.has(k)));
  return order.filter((k) => baseSet.has(k) || nonHideable.has(k));
}

/**
 * 切换某列显隐。只允许切换**白名单内且可隐藏**的列：
 *   - 非白名单 key：原样返回（绝不引入非白名单列）；
 *   - 不可隐藏列（hideable=false，如「操作」）：原样返回（恒可见，禁止隐藏）；
 *   - 其余：开启时按白名单顺序重排以保证列展示稳定，关闭时移除。
 */
export function toggleColumn(
  current: string[],
  key: string,
  columns: EmployeeColumnMeta[],
): string[] {
  const meta = columns.find((c) => c.key === key);
  if (!meta || !meta.hideable) return current;
  if (current.includes(key)) return current.filter((c) => c !== key);
  const order = columns.map((c) => c.key);
  return order.filter((c) => current.includes(c) || c === key);
}

/** 页大小限定在 20/50/100，非法值回退默认 20。 */
export function normalizePageSize(value: number): number {
  return (EMPLOYEE_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : EMPLOYEE_DEFAULT_PAGE_SIZE;
}

export interface EmployeeFilterState {
  keyword: string;
  deptId: string;
  role: Role | '';
  status: EmployeeStatus | '';
  sortBy: 'createdAt' | 'name' | 'lastLoginAt';
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/**
 * UI 筛选状态 → 后端查询参数。空字符串筛选项一律省略（不下发空过滤）；
 * pageSize 收敛到允许集。列可见性是纯展示层状态，不进入查询参数。
 */
export function buildEmployeeListQuery(
  state: EmployeeFilterState,
): AdminEmployeesListQuery {
  return {
    keyword: state.keyword.trim() || undefined,
    deptId: state.deptId || undefined,
    role: state.role || undefined,
    status: state.status || undefined,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    page: state.page,
    pageSize: normalizePageSize(state.pageSize),
  };
}
